import { validateDomain, validateEmail } from '@workspace/seedforge-shared'
import { AtSign, Globe } from 'lucide-react'
import { ExclusionCard } from './exclusion-card.js'
import type { AnalyticsExclusionRow, AnalyticsExclusionType } from './types.js'

export interface AnalyticsExclusionsPanelProps {
  domains: AnalyticsExclusionRow[]
  emails: AnalyticsExclusionRow[]
  onAdd: (type: AnalyticsExclusionType, value: string) => Promise<unknown>
  onRemove: (id: string) => void
  isRemoving: boolean
}

/**
 * Presentational panel for the Settings → Analytics tab. Takes the full
 * exclusion list and add/remove callbacks as props. Splits rows into two
 * cards (domains and emails) and renders them with the shared ExclusionCard.
 */
export function AnalyticsExclusionsPanel({
  domains,
  emails,
  onAdd,
  onRemove,
  isRemoving,
}: AnalyticsExclusionsPanelProps) {
  return (
    <div className="mt-6 space-y-6">
      <div>
        <h2 className="text-lg font-medium text-foreground">Analytics exclusions</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Views from these emails and domains won't be counted in your analytics or AI reports.
          Useful for excluding your own team or test accounts.
        </p>
      </div>

      <ExclusionCard
        title="Excluded Domains"
        icon={<Globe className="size-4 text-muted-foreground" />}
        addLabel="Add domain"
        dialogTitle="Add excluded domain"
        dialogDescription="Views from any email on this domain will be excluded from analytics and AI reports."
        fieldLabel="Domain"
        inputPlaceholder="e.g. acme.com"
        validate={validateDomain}
        rows={domains}
        onAdd={(v) => onAdd('domain', v)}
        onRemove={onRemove}
        isRemoving={isRemoving}
      />

      <ExclusionCard
        title="Excluded Emails"
        icon={<AtSign className="size-4 text-muted-foreground" />}
        addLabel="Add email"
        dialogTitle="Add excluded email"
        dialogDescription="Views from this specific email address will be excluded from analytics and AI reports."
        fieldLabel="Email"
        inputPlaceholder="alice@acme.com"
        validate={validateEmail}
        rows={emails}
        onAdd={(v) => onAdd('email', v)}
        onRemove={onRemove}
        isRemoving={isRemoving}
      />
    </div>
  )
}
